// 전체 화면 UI: 타이틀, 마을 안내, 결과(별·통계·카드 3장), 패배, 일시정지·설정.
import { touchMinCssPx } from './touchMin';
import { Scene, combatScene, type GameCommand, type GameView, type ResultView, type SettingsView } from '../shared/types';
import { gearTotalsOf } from '../shared/gearView';
import { C, FONT, FONT_NUM, button, el, fmtNum, fmtTime } from './dom';
import { LEGAL_LINKS } from '../community/config';
import { installRewardStyle } from './rewardStyle';
import { assetStatus } from '../render/assets';
import { savePrefs, type UiPrefs } from './prefs';
import { audioVolumes, setAudioVolume, type VolumeKind } from '../audio/settings';
import { TILT_RANGE, ZOOM_MIN, loadCameraPrefs, updateCameraPrefs, type CameraPrefs } from '../shared/cameraPrefs';
import { CAMERA, CLEAR_HOLD } from '../shared/config';
import { isRegionId, type MapId, type RegionId } from '../shared/levels';
import { parseHardStage, stageLabel } from '../shared/content/stages';
import { DIFFICULTY_LOOT } from '../shared/content/economy';

/** 결과 화면 골드 배율: 하드 판(D3b)은 클리어·카드 골드 ×3(게임 stageClear와 같은 값), 그 밖 1 */
function goldDiffOf(stageId: string | undefined): number { return parseHardStage(stageId) ? DIFFICULTY_LOOT.hard.gold : 1; }
import { REGIONS } from '../shared/regions';
import { TIER_STYLE } from './regionCard';
import { firstClearItemsText } from './stageMapModel';
import { dailyResultLines } from './dailyModel';
import { trialResultLines } from './trialModel';
import { NEW_FIRST, NEW_STARS, NEW_TIME, RecordWatch, fmtBestTime, retryLabel, rewardBreakdown, rewardBreakdownText, rewardText, resultGoldStat, starsText } from './regionModel';
import { GrowthTracker, defeatLines, resultLines, villageButtonLabel } from './growthModel';
import { VillageQuestCard, setTrackerPref } from './villageQuest';
import { EquipFeedback } from './equipFeedback';
import { QuestWindow } from './questWindow';
import { DailyPanel } from './dailyPanel';
import { loadQuestCardPrefs, updateQuestCardPrefs, type TrackerMode } from './questCardModel';
import { questGoalView, rebirthGoalOn, rebirthGoalView, type QuestGoalView } from './villageQuestModel';
import { villageGoalAction } from './autoQuest';
import { EXP_SAVE_WARNING, EXP_SAVE_WARNING_SHORT, ExpRunTracker, GrowthModsCache, defeatExpLine, expLine, expSavePending, growthOf } from './skillTreeModel';
import { fullscreenSupported, isFullscreen, loadAutoFullscreen, onFullscreenChange, saveAutoFullscreen, toggleFullscreen } from './fullscreen';
import { viewW, viewH } from '../shared/viewport';
import { DAILY_OPEN_EVENT, HELP_EVENT } from './uiEvents';
import { menuBadges } from './menuBar';
import { KEY_PRESETS, KEY_PRESET_DESC, KEY_PRESET_NAME, keymap, type KeyPreset } from '../shared/keymap';
import { loadUiSize, saveUiSize } from './uiSize';
import { pauseKeyText, questTrackerHelp, titleKeyText } from './keyHintText';
import { makeRequestId } from './rpgModel';
import { raidLeftOf, raidResultLines } from './raidModel';
import { equipDef } from '../shared/gear';
import { titleDataText, titleLoadText, titleQueueOnTap, titleShouldAutoStart, titleStartState } from './titleStart';
import { allDataStatus } from '../pwa/regionData';
import { mountAccountSection, type AccountSection } from '../account/index';
import { mountPurchaseHistory } from '../iap/panel';
import { mountPwaSection, type PwaSection } from '../pwa/panel';
import { openSupport } from '../community/index';
import { assetUrl } from '../pwa/assetMap';
import { characterEntry } from './characterEntry';
import { ClearFx } from './clearFx';
import { installClearStyle } from './clearStyle';
import { RewardCards } from './rewardCards';
import { TIMELINE, PICK_SECONDS } from './rewardCardsModel';
import { reducedMotion } from './skillbar';
import { cardLine, parseCardLoot } from '../shared/content/cardReward';

/** 레이드 결과 제목: 하드면 「비룡 토벌 · 하드」 */
function raidTitleText(r: Parameters<typeof raidResultLines>[0]): string {
  const l = raidResultLines(r, (id) => id);
  return l.diffTag ? l.title + ' · ' + l.diffTag : l.title;
}

/** 일시정지 창 바로가기 그림(전체 화면·시야) */
const ICON_FS = '<svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9V3h6M15 3h6v6M21 15v6h-6M9 21H3v-6"/></svg>';
const ICON_ZOOM = '<svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><circle cx="10" cy="10" r="6.5"/><path d="M15 15l5.5 5.5M7 10h6M10 7v6"/></svg>';
const OVERLAY = 'position:absolute;inset:0;display:none;align-items:center;justify-content:center;pointer-events:auto;';
// 타이틀 어둠막(2026-11-03 블라인드 감사 3.0/10 "어둠막이 배경을 거의 가림"): 가운데는 거의 투명하게, 아래 가장자리만 글자·버튼 대비용으로 어둡게(예전 .35→.88)
const TITLE_DIM = 'radial-gradient(ellipse at 50% 38%,rgba(40,60,100,.06) 0%,rgba(10,14,28,.22) 55%,rgba(10,14,28,.55) 100%),linear-gradient(180deg,rgba(10,14,28,0) 55%,rgba(10,14,28,.45) 100%)';
/** 결과 연출 단계: 클리어 연출 → 결과 표 → 카드 뽑기 → 끝 (QA 주소 ?fx=off는 연출 없이 바로 카드, ?pickSec=N은 제한 시간) */
type ResultPhase = 'none' | 'clear' | 'table' | 'cards' | 'done';

export class Screens {
  private readonly title: HTMLDivElement;
  private readonly startBtn: HTMLButtonElement;
  private readonly loadText: HTMLDivElement;
  private readonly assetRetryBtn: HTMLButtonElement;
  private lastLoad = Number.MIN_SAFE_INTEGER;
  private startQueued = false;
  private sendStart: () => void = () => {};
  private readonly startBar: HTMLDivElement;
  private readonly startBarFill: HTMLDivElement;
  /** 마을 퀘스트 카드(지역 이름·북쪽 두 문·다음 목표) */
  private readonly village: VillageQuestCard;
  /** 퀘스트 창(마을 전용 별도 창) */
  private readonly questWin: QuestWindow;
  private villageGoalView: QuestGoalView | null = null;
  private readonly daily: DailyPanel;
  private readonly result: HTMLDivElement;
  private readonly defeat: HTMLDivElement;
  private readonly pause: HTMLDivElement;
  private readonly settingsPanel: HTMLDivElement;
  private readonly toggles: { key: keyof SettingsView; box: HTMLInputElement }[] = [];
  private readonly stars: HTMLSpanElement[] = [];
  private readonly starNotes: HTMLDivElement[] = [];
  private readonly stat: Record<string, HTMLDivElement> = {};
  private readonly resultPanel: HTMLDivElement;
  private clearFx: ClearFx | null = null;
  /** 장비 장착 피드백(가장자리 토스트·장착됨 배지·퀘스트 체크) */
  private readonly equipFx: EquipFeedback;
  /** 클리어 연출 대기 중 건너뛰기 안내(화면 아래 가장자리, 0.8초 뒤부터) */
  private clearSkipHint: HTMLDivElement | null = null;
  private skipTap: (e: PointerEvent) => void = () => {};
  private skipTapOn = false;
  private pickStage: RewardCards | null = null;
  private phase: ResultPhase = 'none';
  private phaseTimer = 0;
  private readonly fxOff: boolean;
  private readonly pickSec: number;
  private kRewardDesc = '';
  private kRewardDaily = '';
  private kDefeatDaily = false;
  /** 일일 결과 문구 캐시(PR #273 Codex 6차: 프레임마다 문구·배열을 새로 만들지 않는다). 결과·보상 값이 바뀔 때만 다시 만든다 */
  private dlLines: ReturnType<typeof dailyResultLines> = null;
  private dlId = ''; private dlKind = ''; private dlCleared = false;
  private dlGold = -1; private dlStones = -1; private dlProtect = -1; private dlGems = -1; private dlFloor = -1;
  private dailyLines(r: ResultView): ReturnType<typeof dailyResultLines> {
    const kind = r.dailyKind ?? '';
    if (r.resultId !== this.dlId || kind !== this.dlKind || r.cleared !== this.dlCleared || r.gold !== this.dlGold || (r.dailyStones ?? 0) !== this.dlStones
      || (r.dailyProtect ?? 0) !== this.dlProtect || (r.dailyGems ?? 0) !== this.dlGems || (r.dailyFloor ?? 0) !== this.dlFloor) {
      this.dlId = r.resultId; this.dlKind = kind; this.dlCleared = r.cleared; this.dlGold = r.gold; this.dlStones = r.dailyStones ?? 0;
      this.dlProtect = r.dailyProtect ?? 0; this.dlGems = r.dailyGems ?? 0; this.dlFloor = r.dailyFloor ?? 0;
      this.dlLines = dailyResultLines(r);
    }
    return this.dlLines;
  }
  /** 고른 카드 문자열 → 골드 아닌 카드 여부 캐시(프레임마다 parseCardLoot 객체를 만들지 않는다, P3-2) */
  private kItemDesc = '';
  private kItemCard = false;
  private readonly resultTitle: HTMLDivElement;
  private readonly resultSub: HTMLDivElement;
  private readonly regionRow: HTMLDivElement;
  private readonly rewardLine: HTMLDivElement;
  private readonly bonusLine: HTMLDivElement;
  /** 레이드 하드 첫 클리어 띠(C13): 칭호·확정 전설·골드·강화석. 하드 첫 클리어 판에서만 보인다 */
  private readonly raidFirstBand: HTMLDivElement;
  private readonly raidFirstItems: HTMLDivElement;
  private kRewardId = '';
  private kRewardSpecial = 0;
  private readonly saveLine: HTMLDivElement;
  /** 결과 화면의 "EXP +X (Lv.a → Lv.b)" 줄과 그 값을 만드는 판별 추적기(던전에 들어갈 때의 확정 EXP를 기억한다) */
  private readonly expLine: HTMLDivElement;
  private readonly run = new ExpRunTracker();
  private readonly modsCache = new GrowthModsCache();
  private kExpXp = -1; private kExpLv = -1; private kDefeatXp = -1; private kDefeatPend = -1;
  private expText = '';
  private saveText = "";
  private readonly defeatRetry: HTMLButtonElement;
  /** 결과 진입 전 기록(새 기록 판정용). 결과·패배 장면에서는 갱신을 멈춘다 */
  private readonly recordWatch = new RecordWatch();
  private kRowMap = ''; private kRowFlags = -1; private kRowTicks = -1; private kRowStars = -1;
  private kRewardMap = ''; private kRewardKills = -1; private kRewardCard = -2; private kRewardCleared = false;
  private kRetryMap = ''; private kDefeatMap = '';
  /** 카드 안내 줄 키(바뀔 때만 문구를 다시 만든다 — 프레임마다 문자열을 만들지 않는다) */
  private kHintOk = false;
  private kHintRaid = false; private kHintDone = false; private kHintPhase: ResultPhase = 'none'; private kHintPicked = -2; private kHintGold = -1; private kHintDesc = ''; private kHintMap = '';
  /** 다음 성장 목표: 입력(프로필·기록·장면)이 바뀔 때만 다시 계산하고, 화면 조각은 자기 version을 따로 기억한다 */
  private readonly growth = new GrowthTracker();
  /** 열린 지역(D3a 순차 해금): 성장 목표가 잠긴 지역의 첫 클리어를 권하지 않게 */
  private readonly regionOpen = (id: RegionId): boolean => (this.view.progress ? this.view.progress.regionUnlocked(id) : id === 'r1' || id === 'r2');
  private readonly doorOpen = (id: string): boolean => (isRegionId(id) ? (this.view.progress ? this.view.progress.regionUnlocked(id) : true) : true);
  private readonly defeatNote: HTMLDivElement;
  private readonly defeatTitle: HTMLDivElement;
  private readonly defeatTip: HTMLDivElement;
  private kDefeatTrialNote = ''; private kResultTrial = '';
  private kVillageVer = -1; private kResultVer = -1; private kDefeatVer = -1; private kResultCleared = false; private kVillageLabel = '';
  private readonly resultBtns: HTMLDivElement;
  private readonly retryBtn: HTMLButtonElement;
  private readonly villageBtn: HTMLButtonElement;
  /** D3a: 다음 스테이지 바로 입장(열린 다음 스테이지가 같은 지역일 때. n-10 첫 클리어는 마을로 → 이동 연출) */
  private readonly nextBtn: HTMLButtonElement;
  private kNext = '-';
  private readonly cardHint: HTMLDivElement;
  private resultKey = -1;
  private picked = -1;
  private lastScale = -1;
  private readonly scalers: HTMLElement[] = [];
  // 타이틀 키아트(2026-11-04): 게임 화면(회전 반영)이 세로면 9:16, 아니면 16:9
  private keyArtEl!: HTMLElement;
  private keyImgEl!: HTMLImageElement;
  private titleLogoEl!: HTMLElement;
  private titleBtnsEl!: HTMLElement;
  private keyLoaded = false;
  private keyPortrait: boolean | null = null;
  private keyW = 0;
  /** 설정 창을 열 때 저장값으로 다시 맞추는 카메라·화면 항목 */
  private readonly camSync: ((p: CameraPrefs) => void)[] = [];

  constructor(
    parent: HTMLElement,
    private readonly view: GameView,
    private readonly send: (c: GameCommand) => void,
    private readonly prefs: UiPrefs,
    private readonly onTouchScale: (s: number) => void,
    private readonly showTouchSetting: boolean,
    /** 터치 버튼 투명도·배치 편집(UX-FLOW 3절). 터치 기기에서만 쓴다 */
    private readonly touchCustom?: { onOpacity: (o: number) => void; onEdit: () => void },
  ) {
    // ── 클리어 연출 대기 건너뛰기: 마지막 적이 쓰러지는 장면을 보여 주는 동안 0.8초 뒤부터 화면을 누르면 결과로 넘어간다
    const skipHint = el('div', 'position:absolute;left:0;right:0;top:max(2px,var(--dh-st,env(safe-area-inset-top)));display:none;justify-content:center;pointer-events:none;z-index:20;', parent);
    skipHint.dataset.testid = 'clear-skip-hint';
    el('div', 'padding:1px 10px;border-radius:10px;background:rgba(8,12,24,.6);color:rgba(255,236,190,.9);font:700 11px ' + FONT + ';letter-spacing:.04em;', skipHint, '화면을 누르면 결과로 넘어갑니다');
    this.clearSkipHint = skipHint;
    // 건너뛰기 탭은 빈 화면(3D 캔버스)을 누를 때만 받는다: 조이스틱·스킬 버튼·창 위 터치는 건너뛰기가 아니다. 대기 중에만 듣는다
    this.skipTap = (e: PointerEvent): void => {
      const f = this.view.flow;
      if (!(e.target instanceof HTMLCanvasElement)) return;
      if (f.clearHold > 0 && f.clearHoldTotal - f.clearHold >= CLEAR_HOLD.skipAfter) this.send({ type: 'skipClear' });
    };
    // ── 타이틀
    this.title = el('div', OVERLAY + 'flex-direction:column;justify-content:space-between;gap:clamp(10px,calc(var(--dh-h,100vh)*.03),26px);box-sizing:border-box;padding:max(16px,env(safe-area-inset-top)) max(16px,var(--dh-sr,env(safe-area-inset-right))) 34px max(16px,var(--dh-sl,env(safe-area-inset-left)));background:' + TITLE_DIM + ';', parent);
    this.title.className = 'dh-title-screen';
    this.title.dataset.testid = 'title-screen';
    // 키아트(2026-11-03 블라인드 감사 P0): 가로는 16:9, 세로는 9:16을 cover로 깐다. 로드 실패하면 층을 숨기고 기존 어둠막 그대로(폴백).
    const keyArt = el('div', 'position:absolute;inset:0;z-index:0;display:none;pointer-events:none;', this.title);
    keyArt.dataset.testid = 'title-key-art';
    const keyImg = el('img', 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:30% 40%;display:block;', keyArt);
    keyImg.alt = '';
    keyImg.draggable = false;
    keyImg.decoding = 'async';
    // 로고·버튼 가독성용 가장자리 그라데이션만(최대 불투명도 .4)
    const keyShade = el('div', 'position:absolute;inset:0;z-index:0;display:none;pointer-events:none;background:linear-gradient(to bottom,rgba(10,14,28,.3) 0%,rgba(10,14,28,0) 28%,rgba(10,14,28,0) 55%,rgba(10,14,28,.4) 100%);', this.title);
    const bgFallback = this.title.style.background;
    this.keyArtEl = keyArt; this.keyImgEl = keyImg;
    keyImg.addEventListener('load', () => { keyArt.style.display = 'block'; keyShade.style.display = 'block'; this.title.style.background = '#0a0e1c'; this.keyLoaded = true; this.keyArtLayout(); });
    keyImg.addEventListener('error', () => { keyArt.style.display = 'none'; keyShade.style.display = 'none'; this.title.style.background = bgFallback; this.keyLoaded = false; this.keyArtLayout(); });
    this.keyArtSync();
    const logo = el('div', 'position:relative;z-index:1;display:flex;align-items:center;justify-content:center;flex:0 1 auto;min-height:0;max-width:100%;text-align:center;', this.title);
    logo.className = 'dh-title-logo';
    logo.dataset.logoState = 'loading';
    // 로고는 디자인 배율과 따로 맞춰 원본 비율을 지키고 낮은 가로 화면의 버튼 공간을 남긴다.
    const logoImage = el('img', 'display:block;width:auto;height:auto;max-width:min(calc(var(--dh-w,100vw)*.82),660px);max-height:min(calc(var(--dh-h,100vh)*.3),240px);object-fit:contain;', logo);
    logoImage.alt = '던전히어로 · DUNGEON HERO';
    logoImage.width = 1536;
    logoImage.height = 512;
    logoImage.draggable = false;
    logoImage.decoding = 'async';
    logoImage.dataset.testid = 'title-logo';
    const logoFallback = el('div', 'display:none;color:#f0e5d1;', logo);
    logoFallback.dataset.testid = 'title-logo-fallback';
    logoFallback.setAttribute('role', 'img');
    logoFallback.setAttribute('aria-label', logoImage.alt);
    el('div', 'font:800 clamp(28px,calc(var(--dh-w,100vw)*.06),46px)/1.2 ' + FONT + ';letter-spacing:.06em;', logoFallback, '던전히어로');
    el('div', 'font:600 clamp(10px,calc(var(--dh-w,100vw)*.02),14px)/1.4 ' + FONT + ';letter-spacing:.25em;margin-top:8px;', logoFallback, 'DUNGEON HERO');
    logoImage.addEventListener('load', () => { logoImage.style.display = 'block'; logoFallback.style.display = 'none'; logo.dataset.logoState = 'ready'; this.keyArtLayout(); });
    logoImage.addEventListener('error', () => { logoImage.style.display = 'none'; logoFallback.style.display = 'block'; logo.dataset.logoState = 'error'; });
    logoImage.src = assetUrl('ui/title/dungeon-hero-logo-v1.webp');
    this.titleLogoEl = logo;
    const bottomGroup = el('div', 'position:relative;z-index:1;display:flex;flex-direction:column;align-items:center;flex:0 0 auto;max-width:100%;', this.title);
    const tb = el('div', 'display:flex;flex-direction:column;gap:12px;align-items:center;', bottomGroup);
    this.titleBtnsEl = tb;
    // 시작은 Kael 모델 로딩이 끝난 뒤 켠다(실패하면 그레이박스로 진행)
    // 준비 전에도 탭을 받는다(disabled 없음): 탭하면 '진입 예약'이 걸리고 준비되는 즉시 자동으로 시작한다(RG-005). 상태는 data-state(ready/loading/queued/failed)
    this.sendStart = () => send({ type: 'start' });
    this.startBtn = button(tb, '시작', () => {
      const i = { ready: assetStatus.ready, failed: assetStatus.failed, queued: this.startQueued };
      if (titleStartState(i).startNow) { this.startQueued = false; this.sendStart(); return; }
      if (titleQueueOnTap(i) && !this.startQueued) { this.startQueued = true; this.lastLoad = Number.MIN_SAFE_INTEGER; this.updateLoading(); }
    }, true, 'start');
    this.startBtn.dataset.state = 'loading';
    this.startBar = el('div', 'width:min(240px,60%);height:4px;border-radius:2px;background:rgba(255,255,255,.18);overflow:hidden;', tb);
    this.startBar.dataset.testid = 'start-progress';
    this.startBarFill = el('div', 'height:100%;width:0%;background:' + C.gold + ';transition:width .2s linear;', this.startBar);
    this.loadText = el('div', 'font:700 13px ' + FONT + ';color:#cfd8f5;min-height:18px;letter-spacing:1px;', tb, '');
    // 캐릭터 모델 실패·시간 초과 때만 보인다(간이 모델로 바로 시작하거나 다시 받기)
    this.assetRetryBtn = button(tb, '다시 시도', () => { const retry = assetStatus.retry; if (retry) retry(); }, false, 'asset-retry');
    this.assetRetryBtn.style.display = 'none';
    // 캐릭터 모드(CHARACTER-PRD §3): 지금 캐릭터 이름#태그와 선택 화면(이름 바꾸기·새 캐릭터·삭제는 거기서)
    const chars = characterEntry();
    if (chars) {
      const cb = button(tb, '', () => chars.open(), false, 'title-character');
      cb.style.fontSize = '16px'; cb.style.padding = '8px 22px';
      const paint = (): void => { cb.textContent = '캐릭터 · ' + chars.label() + (chars.classText() ? ' (' + chars.classText() + ')' : ''); };
      paint();
      chars.onChange(paint);
    }
    button(tb, '설정', () => this.openSettings(true), false, 'settings-title');
    // 키 안내 문구는 지금 키 배치(PC RPG식/액션식)를 따른다 — 배치를 바꾸면 바로 갱신
    const titleKeys = el('div', 'max-width:100%;text-align:center;font:500 13px/1.5 ' + FONT + ';color:#d6def5;text-shadow:0 1px 3px rgba(0,0,0,.9);margin-top:6px;', bottomGroup, titleKeyText(keymap.preset, showTouchSetting));
    keymap.subscribe((p) => { titleKeys.textContent = titleKeyText(p, showTouchSetting); });
    this.scalers.push(tb);
    // 법적 안내 링크 줄(이용약관·청약철회/환불·확률 정보·개인정보처리방침·고객센터): 타이틀 맨 아래 가장자리에 작게. 플레이 영역이 아니라 시작 화면이고 새 탭으로 열린다
    const titleLegal = el('div', 'z-index:1;position:absolute;left:0;right:0;bottom:max(4px,env(safe-area-inset-bottom));display:flex;flex-wrap:wrap;justify-content:center;gap:0 14px;padding:0 12px;pointer-events:none;', this.title);
    titleLegal.dataset.testid = 'title-legal';
    for (const link of LEGAL_LINKS) {
      if (link.href === '/account-delete') continue; // 계정 삭제 안내는 설정 창에 둔다
      const a = el('a', 'pointer-events:auto;font:600 11px ' + FONT + ';color:#8f9dc4;text-decoration:underline;padding:5px 2px;', titleLegal, link.label);
      a.href = link.href; a.target = '_blank'; a.rel = 'noopener noreferrer';
      a.dataset.testid = 'title-legal-' + link.href.slice(1);
    }

    // ── 마을 퀘스트 추적기(왼쪽 체력 바 아래, 기본은 한 줄 — 단 폰은 1-1 클리어 뒤부터 아이콘 하나, resolveTrackerMode) + 별도 퀘스트 창(G). 상단 중앙 안내 상자는 북쪽 문을 가려서 없앴다(2026-10-18).
    // 북쪽 두 문·워프·일일 의뢰는 퀘스트 창에 있고, 문 앞에 서면 지역 카드(regionCard.ts)가 자세히 보여 준다
    this.equipFx = new EquipFeedback(parent, send);
    this.village = new VillageQuestCard(parent, showTouchSetting, () => send({ type: 'openQuest' }));
    this.questWin = new QuestWindow(parent, view, send, showTouchSetting);
    this.daily = new DailyPanel(parent, send);

    // ── 결과
    const qs = typeof location !== 'undefined' ? new URLSearchParams(location.search) : new URLSearchParams();
    // QA 전용(?qa=1일 때만): fx=off는 연출 없이 바로 카드, pickSec=N은 카드 제한 시간(초). 정상 플레이에는 영향이 없다
    const qaOn = qs.has('qa');
    this.fxOff = qaOn && qs.get('fx') === 'off';
    const ps = Number(qs.get('pickSec'));
    this.pickSec = qaOn && Number.isFinite(ps) && ps >= 1 && ps <= 60 ? Math.round(ps) : PICK_SECONDS;
    this.result = el('div', OVERLAY + 'flex-direction:column;gap:14px;background:radial-gradient(ellipse at 50% 35%,rgba(30,42,78,.6),rgba(8,12,24,.9));', parent);
    installRewardStyle();
    installClearStyle();
    this.result.className = 'dh-result';
    this.result.setAttribute('role', 'dialog');
    this.result.setAttribute('aria-label', '던전 클리어 보상');
    const resultPanel = el('div', '', this.result); this.resultPanel = resultPanel; resultPanel.className = 'dh-result-panel'; resultPanel.dataset.testid = 'result-panel';
    const head = el('div', '', resultPanel); head.className = 'dh-result-head';
    const starRow = el('div', 'display:flex;gap:18px;justify-content:center;', head);
    starRow.className = 'dh-result-stars';
    const reasons = ['클리어', '5분 이내', 'HP 50% 이상'];
    for (let i = 0; i < 3; i++) {
      const cell = el('div', 'display:flex;flex-direction:column;align-items:center;gap:2px;', starRow);
      this.stars.push(el('span', 'font-size:54px;line-height:1;color:#3a4260;text-shadow:0 3px 0 #0a0e1c;display:inline-block;', cell, '★')); this.stars[i].className = 'dh-result-star';
      this.starNotes.push(el('div', 'font:700 12px ' + FONT + ';color:#6a7290;', cell, reasons[i]));
    }
    this.resultTitle = el('div', 'font:900 46px ' + FONT + ';color:' + C.gold + ';text-shadow:0 4px 0 #7a4a00,0 0 24px rgba(255,200,80,.5);', head, '던전 클리어');
    this.resultTitle.className = 'dh-result-title';
    // 지역 이름·난이도와 새 기록 표시
    this.regionRow = el('div', 'display:flex;gap:8px;justify-content:center;align-items:center;flex-wrap:wrap;margin:4px 0 2px;min-height:28px;', head);
    this.regionRow.dataset.testid = 'result-region';
    this.resultSub = el('div', 'font:500 15px/1.5 ' + FONT + ';color:#cfd8f5;white-space:pre-line;word-break:keep-all;max-width:calc(var(--dh-w,100vw) - 32px);', head, '');
    this.resultSub.className = 'dh-result-sub';
    this.resultSub.dataset.testid = 'result-sub';
    const panel = el('div', 'display:flex;gap:0;border:2px solid ' + C.gold + ';border-radius:12px;background:rgba(16,22,42,.92);padding:10px 8px;', resultPanel);
    panel.className = 'dh-result-stat';
    const defs: [string, string][] = [['time', '클리어 타임'], ['combo', '최대 콤보'], ['kills', '처치'], ['dmg', '총 피해량'], ['gold', '획득 골드']];
    for (const [k, label] of defs) {
      const cell = el('div', 'padding:2px 22px;text-align:center;border-right:1px solid rgba(242,193,78,.25);', panel);
      el('div', 'font:600 13px ' + FONT + ';color:#aeb9dc;', cell, label);
      this.stat[k] = el('div', 'font:900 28px ' + FONT_NUM + ';color:#fff;margin-top:2px;', cell, '-');
    }
    (panel.lastElementChild as HTMLElement).style.borderRight = 'none';
    this.rewardLine = el('div', 'font:700 15px ' + FONT + ';color:#ffe7a8;text-align:center;min-height:20px;', resultPanel, '');
    this.rewardLine.className = 'dh-result-reward';
    this.rewardLine.dataset.testid = 'result-reward';
    // 첫 클리어 장비·강화석(S3): 첫 클리어일 때만 보인다
    this.bonusLine = el('div', 'font:900 15px ' + FONT + ';color:#9be8ff;text-align:center;display:none;', resultPanel, '');
    this.bonusLine.className = 'dh-result-reward';
    this.bonusLine.dataset.testid = 'result-first-clear';
    this.raidFirstBand = el('div', 'display:none;align-self:center;padding:3px 14px;border-radius:12px;background:linear-gradient(180deg,#ffd76a,#c8963e);border:2px solid #fff3c0;color:#2a1d05;font:900 16px ' + FONT + ';text-align:center;box-shadow:0 0 14px rgba(255,200,80,.55);', resultPanel, '');
    this.raidFirstBand.dataset.testid = 'result-raid-first';
    this.raidFirstItems = el('div', 'display:none;font:800 14px ' + FONT + ';color:#ffd76a;text-align:center;word-break:keep-all;', resultPanel, '');
    this.raidFirstItems.dataset.testid = 'result-raid-first-items';
    this.expLine = el('div', 'display:none;', resultPanel, '');
    this.expLine.className = 'dh-result-exp';
    this.expLine.dataset.testid = 'result-exp';
    this.cardHint = el('div', 'font:700 16px ' + FONT + ';color:' + C.gold + ';', resultPanel, '카드 한 장을 고르세요');
    this.cardHint.className = 'dh-result-hint';
    this.saveLine = el('div', '', resultPanel);
    this.saveLine.className = 'dh-result-save'; this.saveLine.dataset.testid = 'result-save';
    this.saveLine.setAttribute('role', 'status'); this.saveLine.setAttribute('aria-live', 'polite');
    this.resultBtns = el('div', 'display:flex;gap:20px;margin-top:4px;', resultPanel);
    this.resultBtns.className = 'dh-result-actions';
    this.retryBtn = button(this.resultBtns, '다시 도전', () => send({ type: 'retry' }), false, 'retry');
    this.villageBtn = button(this.resultBtns, '마을로', () => send({ type: 'returnToVillage' }), true, 'to-village');
    this.nextBtn = button(this.resultBtns, '다음 스테이지', () => { const r0 = this.view.result; if (r0.dailyKind === 'tower' && (r0.dailyNext ?? 0) > 0) { send({ type: 'enterDaily', kind: 'tower' }); return; } const n = r0.nextStage; if (n) send({ type: 'enterStage', stage: n }); }, true, 'next-stage');
    this.nextBtn.style.display = 'none';
    this.retryBtn.disabled = this.villageBtn.disabled = this.nextBtn.disabled = true;
    this.resultBtns.style.opacity = '0.45';
    this.retryBtn.style.cursor = this.villageBtn.style.cursor = 'not-allowed';


    // ── 패배
    this.defeat = el('div', OVERLAY + 'flex-direction:column;gap:22px;background:radial-gradient(ellipse at 50% 40%,rgba(80,10,20,.55),rgba(8,6,14,.92));', parent);
    this.defeatTitle = el('div', 'font:900 64px ' + FONT + ';color:#ff5a66;text-shadow:0 5px 0 #4a0810,0 0 28px rgba(255,42,61,.55);', this.defeat, '쓰러졌다…');
    // 재도전 요령: 지금 게임에 있는 행동(적 공격 표시·회피·스킬)만, 키보드·터치 공통 문구(UX-FLOW 패배 분기)
    // 일반 고블린은 판정이 표시보다 영웅 몸 반경만큼 넓어 "표시 밖"이 아니라 "넉넉히 벗어나기"(UX-FLOW 패배 분기)
    this.defeatTip = el('div', 'font:500 17px/1.6 ' + FONT + ';color:#e6c3c8;text-align:center;white-space:pre-line;word-break:keep-all;max-width:calc(var(--dh-w,100vw) - 32px);', this.defeat, '공격 표시에서 넉넉히 벗어나거나 회피하세요.\n몰려든 적은 스킬로 한꺼번에 쓸어 내세요.');
    // 확정 골드·보유 장비 유지와 무료 주력 변경 경로(실제 프로필에서 growthModel이 만든다)
    this.defeatNote = el('div', 'font:600 15px/1.6 ' + FONT + ';color:#f0d4d8;text-align:center;white-space:pre-line;word-break:keep-all;max-width:calc(var(--dh-w,100vw) - 32px);', this.defeat, '');
    this.defeatNote.dataset.testid = 'defeat-growth';
    const db = el('div', 'display:flex;gap:20px;', this.defeat);
    this.defeatRetry = button(db, '다시 도전', () => send({ type: 'retry' }), true, 'defeat-retry');
    button(db, '마을로', () => send({ type: 'returnToVillage' }), false, 'defeat-village');
    this.scalers.push(db);

    // ── 일시정지·설정
    this.pause = el('div', OVERLAY + 'background:rgba(6,10,22,.72);', parent);
    const pp = el('div', 'min-width:340px;padding:24px 34px;border:2px solid ' + C.gold + ';border-radius:16px;background:linear-gradient(#222d52,#141b34);display:flex;flex-direction:column;gap:14px;align-items:stretch;box-shadow:0 8px 30px rgba(0,0,0,.6);', this.pause);
    el('div', 'font:900 34px ' + FONT + ';color:' + C.gold + ';text-align:center;', pp, '일시정지');
    button(pp, '재개', () => send({ type: 'setPaused', paused: false }), true, 'resume');
    button(pp, '설정', () => this.openSettings(true), false, 'settings');
    // 화면·카메라 바로가기(2026-10-27: HUD에 따로 떠 있던 전체 화면·카메라 초기화·시야 확대 단추를 설정(톱니)을 누르면 나오는 이 창으로 옮겼다)
    const quick = el('div', 'display:flex;gap:10px;justify-content:center;', pp);
    quick.dataset.testid = 'pause-quick';
    const qbtn = (label: string, icon: string, testid: string, onClick: () => void): HTMLButtonElement => {
      const b = el('button', 'pointer-events:auto;cursor:pointer;flex:1 1 0;min-width:96px;min-height:76px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;padding:6px 4px;' +
        'border-radius:12px;border:2px solid ' + C.gold + ';background:radial-gradient(90% 80% at 50% 30%,#2b3a68,#141c38 70%,#0b1024);color:#fff2c2;font:800 16px ' + FONT + ';white-space:nowrap;', quick);
      b.type = 'button'; b.dataset.testid = testid; b.setAttribute('aria-label', label);
      const ic = el('span', 'display:block;width:36px;height:36px;color:#ffe7a0;', b);
      ic.innerHTML = icon;
      el('span', '', b, label);
      b.addEventListener('click', onClick);
      return b;
    };
    if (fullscreenSupported()) {
      const fsb = qbtn('전체 화면', ICON_FS, 'pause-fullscreen', () => toggleFullscreen());
      fsb.dataset.fsControl = '1';
      const lab = fsb.lastElementChild as HTMLSpanElement;
      onFullscreenChange((on) => { lab.textContent = on ? '창 모드' : '전체 화면'; });
    }
    const camImg = '<img src="' + assetUrl('ui/icons/menu-camera.webp') + '" alt="" style="width:100%;height:100%;object-fit:contain">';
    qbtn('카메라 초기화', camImg, 'pause-camera-reset', () => window.dispatchEvent(new CustomEvent('dh-camera-yaw', { detail: { reset: true } })));
    qbtn('시야 전환', ICON_ZOOM, 'pause-camera-zoom', () => window.dispatchEvent(new CustomEvent('dh-camera-zoom', { detail: 0 })));
    // 의뢰 게시판(일일 의뢰·던전·탑) 바로가기(2026-10-27 18:54 대표님 "의뢰 게시판 같은 건 설정에다 둬 주고"): 마을에서만.
    // 마을 바닥의 실제 게시판 시설·NPC 대화는 그대로 두고, 화면 위에 떠 있던 진입(메뉴 「일일」·게시판 이름표)만 여기로 옮겼다
    this.boardBtn = button(pp, '의뢰 게시판', () => { send({ type: 'setPaused', paused: false }); window.dispatchEvent(new CustomEvent(DAILY_OPEN_EVENT)); }, false, 'pause-board');
    this.boardBtn.insertAdjacentHTML('afterbegin', '<img src="' + assetUrl('ui/icons/menu-daily.webp') + '" alt="" style="width:26px;height:26px;vertical-align:-6px;margin-right:6px;object-fit:contain">');
    this.boardBtn.style.position = 'relative';
    this.boardDot = el('span', 'position:absolute;right:-5px;top:-5px;width:13px;height:13px;border-radius:50%;background:radial-gradient(circle at 35% 30%,#ff9aa0,#e8243a 60%,#8a0f1c);box-shadow:0 0 0 2px rgba(9,12,26,.95);display:none;', this.boardBtn);
    this.boardDot.dataset.testid = 'pause-board-dot';
    const homeBtn = button(pp, '마을로', () => { send({ type: 'setPaused', paused: false }); send({ type: 'returnToVillage' }); }, false, 'pause-village');
    // 홈 그림(메뉴 아이콘 회차) — 글자 왼쪽
    homeBtn.insertAdjacentHTML('afterbegin', '<img src="' + assetUrl('ui/icons/menu-home.webp') + '" alt="" style="width:26px;height:26px;vertical-align:-6px;margin-right:6px;object-fit:contain">');
    // 귀환서(D2c): 던전에서 바닥 드롭을 모두 거두고 마을로(가지고 있을 때만 보인다)
    this.scrollBtn = button(pp, '귀환서 사용', () => { send({ type: 'setPaused', paused: false }); send({ type: 'useReturnScroll', requestId: makeRequestId() }); }, false, 'pause-return-scroll');
    this.scrollBtn.style.display = 'none';
    // 키 안내(HUD 담당 한 줄)
    const pauseKeys = el('div', 'font:500 12px ' + FONT + ';color:#9fb0d8;text-align:center;line-height:1.5;', pp, pauseKeyText(keymap.preset, showTouchSetting));
    pauseKeys.style.whiteSpace = 'pre-line';
    keymap.subscribe((p) => { pauseKeys.textContent = pauseKeyText(p, showTouchSetting); });
    this.scalers.push(pp);

    // 카메라 설정을 바꾸면 뒤 게임 화면이 바로 바뀌므로 배경을 옅게 둔다(일시정지 막은 설정을 여는 동안 숨긴다)
    // 창은 오른쪽에 붙여 가운데의 영웅·카메라 변화가 가려지지 않게 한다
    this.settingsPanel = el('div', OVERLAY + 'justify-content:flex-end;padding:0 24px;box-sizing:border-box;background:linear-gradient(90deg,rgba(6,10,22,0) 30%,rgba(6,10,22,.55));z-index:5;', parent);
    // 항목이 많아 모바일 가로 화면에서는 창 안에서 스크롤한다
    const sp = el('div', 'min-width:380px;max-height:calc(100% - 24px);overflow-y:auto;overscroll-behavior:contain;touch-action:pan-x pan-y;box-sizing:border-box;padding:0 34px 24px;border:2px solid ' + C.gold + ';border-radius:16px;background:linear-gradient(#222d52,#141b34);display:flex;flex-direction:column;gap:14px;box-shadow:0 8px 30px rgba(0,0,0,.6);', this.settingsPanel);
    // RG-029: 세로 폰에서는 #app이 90도 돌아 있어 touch-action:pan-y(화면 기준 세로)가 창 안쪽 스크롤 방향(화면 가로)을 막았다 → pan-x pan-y.
    // 닫기 단추는 긴 본문 맨 아래에만 있어 화면 밖으로 밀렸으므로 스크롤해도 따라오는 고정 머리줄(sticky)에 ✕도 둔다.
    const setHead = el('div', 'position:sticky;top:0;z-index:2;display:flex;align-items:center;justify-content:center;padding:24px 0 10px;background:#222d52;border-radius:14px 14px 0 0;', sp);
    el('div', 'font:900 30px ' + FONT + ';color:' + C.gold + ';text-align:center;', setHead, '설정');
    const topClose = el('button', 'position:absolute;right:-22px;top:18px;width:48px;height:48px;box-sizing:border-box;padding:0;border-radius:50%;border:2px solid ' + C.gold + ';background:linear-gradient(#26335a,#1A2238);color:' + C.gold + ';font:900 22px ' + FONT + ';cursor:pointer;pointer-events:auto;touch-action:manipulation;', setHead, '✕');
    topClose.dataset.testid = 'settings-close-top'; topClose.setAttribute('aria-label', '설정 닫기');
    topClose.addEventListener('click', () => this.openSettings(false));
    // 계정·서버 저장(D5a): 게스트/Google 연결·마지막 동기화·연결·삭제(src/account/panel.ts)
    this.account = mountAccountSection(sp);
    // 구매 내역·청약철회 요청(인앱 결제 — 결제 설정이 있을 때만 보인다, src/iap/panel.ts)
    mountPurchaseHistory(sp);
    // 앱 설치·새 버전 안내(자산 패치·PWA, 서비스 워커를 쓸 수 없는 환경에서는 숨는다)
    this.pwa = mountPwaSection(sp);
    // 고객센터(D5b): 버그 신고·건의·1:1 문의·내 글 답변. 마을에서만 열린다(던전이면 안내 띠)
    button(sp, '고객센터 · 버그 신고', () => { if (openSupport()) this.openSettings(false); }, false, 'settings-support');
    // 개인정보처리방침·이용약관·계정 삭제 안내(정적 페이지, 새 탭)
    const legal = el('div', 'display:flex;flex-wrap:wrap;justify-content:center;gap:6px 18px;', sp);
    legal.dataset.testid = 'settings-legal';
    for (const link of LEGAL_LINKS) {
      const a = el('a', 'pointer-events:auto;font:600 13px ' + FONT + ';color:#9fb0d8;text-decoration:underline;padding:6px 2px;', legal, link.label);
      a.href = link.href; a.target = '_blank'; a.rel = 'noopener noreferrer';
      a.dataset.testid = 'legal-' + link.href.slice(1);
    }
    const rows: [keyof SettingsView, string, string][] = [
      ['mature', '성인 연출(18+)', '피 튀김 표현을 켭니다'],
      ['reduceFlash', '섬광 약화', '화면 번쩍임을 줄입니다'],
      ['skipCutin', '컷인 생략', '두 번째 각성부터 컷인을 건너뜁니다'],
    ];
    for (const [key, label, desc] of rows) {
      const row = el('label', 'display:flex;align-items:center;gap:14px;cursor:pointer;', sp);
      const box = el('input', 'width:24px;height:24px;accent-color:' + C.gold + ';cursor:pointer;', row);
      box.type = 'checkbox';
      box.dataset.testid = 'setting-' + key;
      box.addEventListener('change', () => send({ type: 'setSetting', key, value: box.checked }));
      const t = el('div', '', row);
      el('div', 'font:800 18px ' + FONT + ';color:#fff;', t, label);
      el('div', 'font:500 12px ' + FONT + ';color:#9fb0d8;', t, desc);
      this.toggles.push({ key, box });
    }
    // 소리 크기(마스터·BGM·효과음). 저장: localStorage 'dungeonhero.audio'(src/audio/settings.ts)
    const volRows: [VolumeKind, string][] = [['master', '전체 소리'], ['bgm', '배경 음악'], ['sfx', '효과음']];
    for (const [kind, label] of volRows) {
      const row = el('div', 'display:flex;align-items:center;gap:12px;', sp);
      el('div', 'font:800 18px ' + FONT + ';color:#fff;min-width:96px;', row, label);
      const slider = el('input', 'flex:1;accent-color:' + C.gold + ';cursor:pointer;', row);
      slider.type = 'range';
      slider.min = '0'; slider.max = '1'; slider.step = '0.05';
      slider.value = String(audioVolumes()[kind]);
      slider.dataset.testid = 'setting-volume-' + kind;
      slider.setAttribute('aria-label', label);
      const pct = el('div', 'font:700 15px ' + FONT_NUM + ';color:' + C.gold + ';min-width:44px;text-align:right;', row, Math.round(audioVolumes()[kind] * 100) + '%');
      slider.addEventListener('input', () => {
        setAudioVolume(kind, Number(slider.value));
        pct.textContent = Math.round(audioVolumes()[kind] * 100) + '%';
      });
    }
    this.buildCameraSettings(sp, showTouchSetting);
    if (showTouchSetting) {
      const row = el('div', 'display:flex;flex-direction:column;gap:6px;', sp);
      el('div', 'font:800 18px ' + FONT + ';color:#fff;', row, '터치 버튼 크기');
      const slider = el('input', 'width:100%;accent-color:' + C.gold + ';', row);
      slider.type = 'range';
      slider.min = '0.7'; slider.max = '1.5'; slider.step = '0.05';
      slider.value = String(prefs.touchScale);
      slider.dataset.testid = 'setting-touch-scale';
      slider.addEventListener('input', () => {
        prefs.touchScale = Number(slider.value);
        savePrefs(prefs);
        onTouchScale(prefs.touchScale);
      });
      // 투명도: 버튼 뒤 전투가 비쳐 보이게(누르는 영역은 그대로)
      const tc = this.touchCustom;
      if (tc) {
        const orow = el('div', 'display:flex;flex-direction:column;gap:6px;', sp);
        const ohead = el('div', 'display:flex;justify-content:space-between;align-items:baseline;', orow);
        el('div', 'font:800 18px ' + FONT + ';color:#fff;', ohead, '터치 버튼 투명도');
        const opct = el('div', 'font:700 15px ' + FONT_NUM + ';color:' + C.gold + ';', ohead, Math.round(prefs.touchOpacity * 100) + '%');
        const os = el('input', 'width:100%;accent-color:' + C.gold + ';', orow);
        os.type = 'range'; os.min = '0.35'; os.max = '1'; os.step = '0.05';
        os.value = String(prefs.touchOpacity);
        os.dataset.testid = 'setting-touch-opacity';
        os.setAttribute('aria-label', '터치 버튼 투명도');
        os.addEventListener('input', () => {
          prefs.touchOpacity = Number(os.value);
          opct.textContent = Math.round(prefs.touchOpacity * 100) + '%';
          savePrefs(prefs);
          tc.onOpacity(prefs.touchOpacity);
        });
        // 배치 편집: 설정 창을 닫고 편집기를 연다(편집기가 게임을 멈추고, 저장하면 바로 적용·저장)
        button(sp, '터치 버튼 배치 편집', () => { this.openSettings(false); tc.onEdit(); }, false, 'setting-touch-edit');
      }
    }
    // 렌더 품질·렌더러(다음 실행부터 적용)
    const selRow = (label: string, options: [string, string][], value: string, testId: string, onChange: (v: string) => void): void => {
      const row = el('div', 'display:flex;align-items:center;justify-content:space-between;gap:12px;', sp);
      el('div', 'font:800 18px ' + FONT + ';color:#fff;', row, label);
      const sel = el('select', 'pointer-events:auto;font:700 15px ' + FONT + ';padding:4px 8px;border-radius:8px;border:2px solid ' + C.gold + ';background:#1A2238;color:' + C.gold + ';', row);
      sel.dataset.testid = testId;
      for (const [v, t] of options) {
        const o = el('option', '', sel, t);
        o.value = v;
      }
      sel.value = value;
      sel.addEventListener('change', () => onChange(sel.value));
    };
    // 키 배치(레인 C): PC RPG식(K 스킬·C 스탯) / 액션식(K 회피·C 카메라). 바로 적용·기기 전용 저장(shared/keymap.ts). 터치 기기에서도 보인다(블루투스 키보드)
    {
      const krow = el('div', 'display:flex;flex-direction:column;gap:8px;', sp);
      krow.dataset.testid = 'setting-keymap';
      el('div', 'font:800 18px ' + FONT + ';color:#fff;', krow, '키 배치');
      const kbtns = el('div', 'display:flex;gap:8px;', krow);
      const kdesc = el('div', 'font:500 13px/1.45 ' + FONT + ';color:#9fb0d8;', krow, KEY_PRESET_DESC[keymap.preset]);
      const kb = new Map<KeyPreset, HTMLButtonElement>();
      const paint = (cur: KeyPreset): void => {
        for (const [pr, b] of kb) {
          const on = pr === cur;
          b.style.background = on ? C.gold : '#1A2238';
          b.style.color = on ? '#2a1d05' : C.gold;
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        }
        kdesc.textContent = KEY_PRESET_DESC[cur];
      };
      for (const pr of KEY_PRESETS) {
        const b = el('button', 'pointer-events:auto;cursor:pointer;flex:1 1 0;min-height:40px;padding:4px 8px;border-radius:10px;border:2px solid ' + C.gold + ';font:800 15px ' + FONT + ';', kbtns, KEY_PRESET_NAME[pr]);
        b.type = 'button';
        b.dataset.testid = 'setting-keymap-' + pr;
        b.addEventListener('click', () => keymap.set(pr));
        kb.set(pr, b);
      }
      paint(keymap.preset);
      keymap.subscribe(paint);
    }
    // 자동 물약(D2c): HP가 문턱 아래로 내려가면 쿨다운이 끝났을 때 HP 퀵슬롯을 쓴다. 바로 적용(게임 명령) + UI 설정에 저장
    selRow('자동 물약', [['0', '끄기'], ['0.3', 'HP 30% 아래'], ['0.5', 'HP 50% 아래']], String(prefs.autoPotion), 'setting-auto-potion', (v) => {
      prefs.autoPotion = v === '0.3' ? 0.3 : v === '0.5' ? 0.5 : 0;
      savePrefs(prefs);
      send({ type: 'setAutoPotion', pct: prefs.autoPotion });
    });
    selRow('화질', [['auto', '자동'], ['low', '낮음'], ['mid', '중간'], ['high', '높음']], prefs.quality, 'setting-quality', (v) => {
      prefs.quality = v as UiPrefs['quality'];
      savePrefs(prefs);
      reloadNote.style.display = 'block';
    });
    selRow('렌더러', [['auto', '자동(WebGPU 우선)'], ['webgl', 'WebGL2 고정']], prefs.renderer, 'setting-renderer', (v) => {
      prefs.renderer = v as UiPrefs['renderer'];
      savePrefs(prefs);
      reloadNote.style.display = 'block';
    });
    const reloadNote = el('div', 'display:none;font:600 13px ' + FONT + ';color:' + C.gold + ';text-align:center;', sp, '화질·렌더러는 다음 실행부터 적용됩니다.');
    button(sp, '닫기', () => this.openSettings(false), true, 'settings-close');
  }

  private settingsOpen = false;
  /** 일시정지 창 「의뢰 게시판」(마을만)과 받을 보상 점 */
  private boardBtn!: HTMLButtonElement;
  private boardDot!: HTMLSpanElement;
  private kBoard = -1;
  private account!: AccountSection;
  private pwa!: PwaSection;
  /** 일시정지 창 귀환서 버튼(D2c)과 마지막으로 그린 보유 수 */
  private scrollBtn!: HTMLButtonElement;
  private kScrolls = -1;

  private dataAt = -1e9;
  private updateLoading(): void {
    const s = assetStatus;
    // 예약된 진입: 정상 준비되면 한 번만 시작을 보낸다. 실패면 예약을 풀고 아래 실패 안내를 보인다
    if (this.startQueued) {
      const i = { ready: s.ready, failed: s.failed, queued: true };
      if (titleShouldAutoStart(i)) { this.startQueued = false; this.sendStart(); return; }
      if (s.failed && !s.ready) this.startQueued = false;
    }
    const done = s.ready || s.failed;
    // 캐릭터 모델이 준비되면 그 자리에 게임 데이터 전체 받기 진행을 보인다(시작 조건이 아니다). 0.5초마다만 센다
    if (s.ready) {
      const t = performance.now();
      if (t - this.dataAt > 500) {
        this.dataAt = t;
        const dt = titleDataText(allDataStatus());
        if (dt !== this.loadText.textContent) this.loadText.textContent = dt;
      }
    }
    const key = (done ? (s.ready ? -1 : -3 - s.attempts) : Math.floor(s.progress * 100)) * 2 + (this.startQueued ? 1 : 0);
    if (key === this.lastLoad) return;
    this.lastLoad = key;
    const st = titleStartState({ ready: s.ready, failed: s.failed, queued: this.startQueued });
    this.startBtn.dataset.state = st.state;
    this.startBtn.textContent = st.label;
    this.startBtn.setAttribute('aria-busy', st.state === 'queued' || st.state === 'loading' ? 'true' : 'false');
    // 준비 전에도 흐린 비활성처럼 보이지 않는다. 예약되면 눌린 모양
    this.startBtn.style.opacity = '1';
    this.startBtn.style.cursor = 'pointer';
    this.startBtn.style.filter = st.state === 'queued' ? 'brightness(.85)' : '';
    this.startBtn.style.transform = st.state === 'queued' ? 'scale(.97)' : '';
    this.startBar.style.display = done ? 'none' : '';
    this.startBarFill.style.width = Math.floor(s.progress * 100) + '%';
    this.assetRetryBtn.style.display = !s.ready && s.failed && s.retry ? '' : 'none';
    this.loadText.textContent = s.ready ? titleDataText(allDataStatus()) : s.failed
      ? '캐릭터 모델을 불러오지 못했어요 · 다시 시도하거나 간이 모델로 시작할 수 있어요'
      : titleLoadText(s.progress, s.attempts, this.startQueued);
  }

  private openSettings(open: boolean): void {
    this.settingsOpen = open;
    this.settingsPanel.style.display = open ? 'flex' : 'none';
    if (open) {
      this.account.refresh();
      this.pwa.refresh();
      const p = loadCameraPrefs();
      for (const f of this.camSync) f(p);
    }
  }

  /**
   * 카메라·화면 설정(COMBAT-FEEL 7장, UX-FLOW 설정): 시야 거리·카메라 높이·전투 연출·타격 흔들림·두 손가락 줌·전체 화면.
   * 값은 바로 적용된다(카메라가 'dh-camera-prefs'를 듣는다). 저장: localStorage 'dungeonhero.camera', 'dungeonhero.display'
   */
  private buildCameraSettings(sp: HTMLElement, touch: boolean): void {
    const head = (text: string): void => {
      el('div', 'margin-top:4px;padding-top:10px;border-top:1px solid rgba(242,193,78,.3);font:900 15px ' + FONT + ';letter-spacing:2px;color:' + C.gold + ';', sp, text);
    };
    const slider = (label: string, min: number, max: number, step: number, testId: string, show: (v: number) => string, read: (p: CameraPrefs) => number, write: (v: number) => void): void => {
      const row = el('div', 'display:flex;align-items:center;gap:12px;', sp);
      el('div', 'font:800 18px ' + FONT + ';color:#fff;min-width:96px;', row, label);
      const s = el('input', 'flex:1;min-height:28px;accent-color:' + C.gold + ';cursor:pointer;touch-action:pan-x;', row);
      s.type = 'range';
      s.min = String(min); s.max = String(max); s.step = String(step);
      s.dataset.testid = testId;
      s.setAttribute('aria-label', label);
      const out = el('div', 'font:700 15px ' + FONT_NUM + ';color:' + C.gold + ';min-width:56px;text-align:right;', row, '');
      const sync = (p: CameraPrefs): void => { const v = read(p); s.value = String(v); out.textContent = show(v); };
      s.addEventListener('input', () => { const v = Number(s.value); out.textContent = show(v); write(v); });
      this.camSync.push(sync);
      sync(loadCameraPrefs());
    };
    const select = (label: string, options: [string, string][], testId: string, read: (p: CameraPrefs) => string, write: (v: string) => void): void => {
      const row = el('div', 'display:flex;align-items:center;justify-content:space-between;gap:12px;', sp);
      el('div', 'font:800 18px ' + FONT + ';color:#fff;', row, label);
      const sel = el('select', 'pointer-events:auto;min-height:34px;font:700 15px ' + FONT + ';padding:4px 8px;border-radius:8px;border:2px solid ' + C.gold + ';background:#1A2238;color:' + C.gold + ';', row);
      sel.dataset.testid = testId;
      sel.setAttribute('aria-label', label);
      for (const [v, t] of options) { const o = el('option', '', sel, t); o.value = v; }
      sel.addEventListener('change', () => write(sel.value));
      const sync = (p: CameraPrefs): void => { sel.value = read(p); };
      this.camSync.push(sync);
      sync(loadCameraPrefs());
    };
    const check = (label: string, desc: string, testId: string, read: () => boolean, write: (v: boolean) => void, attrs?: Record<string, string>): void => {
      const row = el('label', 'display:flex;align-items:center;gap:14px;cursor:pointer;', sp);
      const box = el('input', 'width:24px;height:24px;accent-color:' + C.gold + ';cursor:pointer;', row);
      box.type = 'checkbox';
      box.dataset.testid = testId;
      if (attrs) for (const k in attrs) row.setAttribute(k, attrs[k]);
      box.addEventListener('change', () => write(box.checked));
      const t = el('div', '', row);
      el('div', 'font:800 18px ' + FONT + ';color:#fff;', t, label);
      el('div', 'font:500 12px ' + FONT + ';color:#9fb0d8;', t, desc);
      const sync = (): void => { box.checked = read(); };
      this.camSync.push(sync);
      sync();
    };

    head('카메라');
    slider('시야 거리', ZOOM_MIN, 1, 0.05, 'setting-camera-zoom', (v) => (v <= ZOOM_MIN + 0.03 ? '근접(어깨 너머)' : v < -0.03 ? '근접 ' + Math.round(-v * 100) + '%' : v < 0.03 ? '기본' : v > 0.97 ? '넓게' : Math.round(v * 100) + '%'), (p) => p.zoom, (v) => updateCameraPrefs({ zoom: v }));
    slider('카메라 높이', -TILT_RANGE, TILT_RANGE, 1, 'setting-camera-tilt', (v) => CAMERA.pitchDeg + v + '°', (p) => p.tilt, (v) => updateCameraPrefs({ tilt: v }));
    select('전투 연출', [['off', '끔'], ['low', '약하게'], ['normal', '보통'], ['strong', '강하게']], 'setting-camera-cine', (p) => p.cine, (v) => updateCameraPrefs({ cine: v as CameraPrefs['cine'] }));
    select('타격 흔들림', [['off', '끔'], ['low', '약하게'], ['normal', '보통']], 'setting-camera-shake', (p) => p.shake, (v) => updateCameraPrefs({ shake: v as CameraPrefs['shake'] }));
    check('카메라 좌우 반전', '켜면 드래그 방향과 시야 회전 방향이 반대가 됩니다. 기본은 왼쪽으로 끌면 시야가 왼쪽으로 돕니다', 'setting-camera-invert', () => loadCameraPrefs().invertYaw, (v) => { updateCameraPrefs({ invertYaw: v }); });
    if (touch) check('두 손가락 줌', '화면 빈 곳을 두 손가락으로 벌리거나 좁혀 시야 거리를 바꿉니다', 'setting-camera-pinch', () => loadCameraPrefs().pinch, (v) => { updateCameraPrefs({ pinch: v }); });
    // 시야 거리는 HUD 버튼·핀치·휠로도 바뀐다: 설정 창이 열려 있으면 슬라이더를 맞춘다
    window.addEventListener('dh-camera-zoomed', () => { if (this.settingsOpen) { const p = loadCameraPrefs(); for (const f of this.camSync) f(p); } });

    // 마을 왼쪽 퀘스트 추적기: 표시·모양. 추적기의 접기 단추로도 바뀐다(저장: localStorage 'dungeonhero.ui.questCard'). 자세한 내용은 퀘스트 창(G)
    head('퀘스트 추적기');
    check('추적기 표시', questTrackerHelp(touch), 'setting-quest-visible', () => loadQuestCardPrefs().visible, (v) => { updateQuestCardPrefs({ visible: v }); });
    select('추적기 모양', [['auto', '자동 (처음엔 한 줄 · 폰은 이후 아이콘)'], ['line', '한 줄'], ['min', '아이콘 하나']], 'setting-quest-size',
      () => loadQuestCardPrefs().tracker ?? 'auto', (v) => { setTrackerPref(v === 'auto' ? null : (v as TrackerMode)); });
    if (!touch) {
      head('PC');
      select('UI 크기', [['0.9', '90%'], ['1', '100%'], ['1.15', '115%']], 'setting-ui-size', () => String(loadUiSize()), (v) => { saveUiSize(Number(v)); });
      const kb = el('button', 'pointer-events:auto;cursor:pointer;min-height:36px;align-self:flex-start;font:800 15px ' + FONT + ';padding:6px 16px;border-radius:10px;border:2px solid ' + C.gold + ';background:#1A2238;color:' + C.gold + ';', sp, '단축키 안내 (F1)');
      kb.dataset.testid = 'setting-help';
      kb.addEventListener('click', () => window.dispatchEvent(new CustomEvent(HELP_EVENT)));
    }

    if (fullscreenSupported()) {
      head('화면');
      const row = el('div', 'display:flex;align-items:center;justify-content:space-between;gap:12px;', sp);
      el('div', 'font:800 18px ' + FONT + ';color:#fff;', row, '전체 화면');
      const fsBtn = el('button', 'pointer-events:auto;cursor:pointer;min-height:36px;font:800 15px ' + FONT + ';padding:6px 16px;border-radius:10px;border:2px solid ' + C.gold + ';background:#1A2238;color:' + C.gold + ';', row, '');
      fsBtn.dataset.testid = 'setting-fullscreen';
      fsBtn.dataset.fsControl = '1';
      const label = (): void => { fsBtn.textContent = isFullscreen() ? '끄기' : '켜기'; };
      fsBtn.addEventListener('click', () => toggleFullscreen());
      onFullscreenChange(label);
      label();
      check('시작할 때 전체 화면', '첫 터치에서 주소창을 걷고 화면을 가득 채웁니다', 'setting-auto-fullscreen', () => loadAutoFullscreen(touch), (v) => saveAutoFullscreen(v), { 'data-fs-control': '1' });
    }
  }

  private pickCard(i: number): void {
    if (this.picked >= 0 || this.view.result.pickedCard >= 0) return;
    this.picked = i;
    this.send({ type: 'pickCard', index: i });
  }

  /** 결과 진입: 연출 순서 정하기(레이드·이미 고른 결과는 연출 없이 표만, 이어받은 결과는 짧게) */
  private beginResultFlow(): void {
    const r = this.view.result;
    this.cancelFlow();
    this.kHintOk = false;
    if (r.raid || r.pickedCard >= 0) { this.phase = 'done'; return; }
    const quick = this.fxOff || r.restored || !r.cleared;
    if (quick) { this.enterTable(this.fxOff ? 0 : TIMELINE.tableRestoredMs); return; }
    this.phase = 'clear';
    this.resultPanel.classList.add('dh-hidden');
    if (!this.clearFx) this.clearFx = new ClearFx(this.result, reducedMotion());
    this.clearFx.play({ starMask: Number.isFinite(r.starMask) && r.starMask >= 0 ? r.starMask : (1 << Math.max(0, r.stars)) - 1, reduceFlash: this.view.settings.reduceFlash, reducedMotion: reducedMotion() }, () => this.enterTable(TIMELINE.tableMs));
  }

  private enterTable(holdMs: number): void {
    this.phase = 'table';
    const p = this.resultPanel;
    p.classList.remove('dh-hidden');
    if (holdMs > 0 && !reducedMotion()) { p.classList.remove('dh-rise'); void p.offsetWidth; p.classList.add('dh-rise'); }
    window.clearTimeout(this.phaseTimer);
    if (holdMs <= 0) { this.enterCards(); return; }
    this.phaseTimer = window.setTimeout(() => this.enterCards(), holdMs);
  }

  private enterCards(): void {
    const r = this.view.result;
    if (this.phase === 'none' || this.phase === 'done' || r.pickedCard >= 0) { this.phase = 'done'; return; }
    this.phase = 'cards';
    if (!this.pickStage) this.pickStage = new RewardCards(this.result, reducedMotion());
    this.pickStage.open({
      resultId: r.resultId, gold: Array.from(r.cards), loot: r.cardLoot ? Array.from(r.cardLoot) : [], gearName: (id) => equipDef(id)?.name ?? id,
      reduceFlash: this.view.settings.reduceFlash, reducedMotion: reducedMotion(), instant: this.fxOff, seconds: this.pickSec,
    }, (i) => this.pickCard(i), () => { this.phase = 'done'; });
  }

  /** 결과 장면을 벗어났다: 연출·타이머·카드 화면을 끊고 표를 되살린다 */
  private cancelFlow(): void {
    window.clearTimeout(this.phaseTimer);
    this.clearFx?.cancel();
    this.pickStage?.cancel();
    this.resultPanel.classList.remove('dh-hidden', 'dh-rise');
    this.phase = 'none';
  }

  /** u: 디자인 해상도 배율 */
  /** 게임 화면이 세로인지에 따라 키아트 원본을 고른다(바뀔 때만 src 교체) */
  private keyArtSync(): void {
    const portrait = viewW() < viewH();
    if (portrait === this.keyPortrait) return;
    this.keyPortrait = portrait;
    this.keyImgEl.style.objectPosition = portrait ? '50% 60%' : '30% 40%';
    this.keyImgEl.src = assetUrl(portrait ? 'ui/title/key-9x16.webp' : 'ui/title/key-16x9.webp');
  }

  /** 가로 키아트는 영웅이 왼쪽이므로 로고·버튼을 오른쪽 빈 하늘·강 쪽으로 옮긴다(세로는 그대로 가운데) */
  private keyArtLayout(): void {
    const w = viewW();
    this.keyW = w;
    const shift = this.keyLoaded && !this.keyPortrait ? Math.max(0, Math.min(w * 0.12, (w - this.titleLogoEl.offsetWidth) / 2 - 12)) : 0;
    const t = shift > 0 ? 'translateX(' + Math.round(shift) + 'px)' : '';
    this.titleLogoEl.style.transform = t;
    this.titleBtnsEl.style.transform = t;
  }

  update(u: number): void {
    const v = this.view;
    const sc = v.flow.scene;
    // 결과·패배가 아닌 동안 기록을 기억해 둔다(결과 진입 때는 이미 갱신돼 있다)
    this.recordWatch.observe(sc, v.records);
    this.title.style.display = sc === Scene.Title ? 'flex' : 'none';
    if (sc === Scene.Title) this.updateLoading();
    this.result.style.display = sc === Scene.Result ? 'flex' : 'none';
    if (this.clearSkipHint) {
      const f = v.flow, show = sc === Scene.Dungeon && f.clearHold > 0 && f.clearHoldTotal - f.clearHold >= CLEAR_HOLD.skipAfter;
      const d = show ? 'flex' : 'none';
      if (this.clearSkipHint.style.display !== d) this.clearSkipHint.style.display = d;
      const hold = sc === Scene.Dungeon && f.clearHold > 0;
      if (hold !== this.skipTapOn) {
        this.skipTapOn = hold;
        if (hold) window.addEventListener('pointerdown', this.skipTap, true); else window.removeEventListener('pointerdown', this.skipTap, true);
      }
    }
    this.defeat.style.display = sc === Scene.Defeat ? 'flex' : 'none';
    const paused = v.flow.paused && (sc === Scene.Village || combatScene(sc));
    this.pause.style.display = paused && !this.settingsOpen ? 'flex' : 'none';
    const board = paused && sc === Scene.Village ? (menuBadges.daily ? 2 : 1) : 0;
    if (board !== this.kBoard) { this.kBoard = board; this.boardBtn.style.display = board ? '' : 'none'; this.boardDot.style.display = board === 2 ? 'block' : 'none'; }
    // 귀환서 버튼: 던전 일시정지 + 가지고 있을 때만(보유 수가 바뀔 때만 글자를 쓴다)
    const scrolls = paused && combatScene(sc) && v.gear ? v.gear.cons['scroll_return'] ?? 0 : 0;
    if (scrolls !== this.kScrolls) {
      this.kScrolls = scrolls;
      this.scrollBtn.style.display = scrolls > 0 ? '' : 'none';
      if (scrolls > 0) this.scrollBtn.textContent = '귀환서 사용 (' + scrolls + '장 · 드롭 수거)';
    }
    if (sc !== Scene.Title && !paused && this.settingsOpen) this.openSettings(false);

    if (sc === Scene.Title) { this.keyArtSync(); if (viewW() !== this.keyW) this.keyArtLayout(); }
    if (u !== this.lastScale) {
      this.lastScale = u;
      // 좁고 낮은 화면에서도 성장 부제 아래 카드·이동 버튼이 잘리지 않게 간격도 디자인 배율을 따른다.
      this.result.style.gap = Math.max(2, Math.round((viewH() < 400 ? 5 : 14) * u)) + 'px';
      for (const s of this.scalers) s.style.zoom = String(u);
      // 터치 기기: 일시정지 창이 배율로 줄어도 단추 겉 높이가 44px 아래로 내려가지 않게 한다(출시 게이트 RG-008)
      if (this.showTouchSetting) {
        const mh = touchMinCssPx(u) + 'px';
        for (const b of this.pause.querySelectorAll<HTMLElement>('button[data-testid="resume"],button[data-testid="settings"],button[data-testid^="pause-"]')) b.style.minHeight = mh;
      }
    }
    if (this.settingsOpen) {
      const s = v.settings;
      for (const t of this.toggles) if (t.box.checked !== s[t.key]) t.box.checked = s[t.key];
    }
    // 이번 판 EXP: 던전에 들어갈 때의 확정 EXP를 기억해 결과·패배 화면에서 "EXP +X (Lv.a → Lv.b)"를 만든다
    const gv = growthOf(v);
    if (gv) this.run.observe(combatScene(sc), v.flow.sceneSerial, gv.xp, gv.pendingXp);
    // 성장 목표는 마을·결과·패배에서만 쓴다. 입력이 바뀐 프레임에만 다시 계산한다
    if (sc === Scene.Village || sc === Scene.Result || sc === Scene.Defeat) this.growth.sync(sc, v.result.pickedCard >= 0, v.profile, v.records, this.modsCache.get(gv), gv ? gv.revision : 0, gearTotalsOf(v.gear, v.heroId), v.gear ? v.gear.revision : 0, this.regionOpen, v.progress ? v.progress.cleared : 0, v.flow.town, v.gear, v.heroId ?? 'kael');
    this.equipFx.update(v, this.growth.goal.upgrades);
    if (sc === Scene.Village) this.updateVillageGoal();
    else this.kVillageVer = -1;
    this.village.update(u, sc === Scene.Village, v.flow.stageLabel, this.villageGoalView, v.flow.town ?? 1, v.adventure ?? null, v.progress ? v.progress.cleared : -1);
    this.questWin.update(u, this.villageGoalView, v.flow.stageLabel, this.doorOpen, v.flow.town ?? 1, v.progress ? v.progress.highestTown : 1);
    // 일시정지(톱니)·도움말을 열면 일일 게시판은 닫는다(일시정지 창 위에 남아 가리던 문제, 2026-10-27 QA)
    this.daily.update(sc === Scene.Village, v.progress ? v.progress.highestTown : 1, v.result.resultId, v.result.kills, sc === Scene.Result || sc === Scene.Defeat, v.flow.paused, v.adventure);
    if (sc === Scene.Result) this.updateResult();
    else { if (this.resultKey !== -1) this.cancelFlow(); this.resultKey = -1; this.kResultVer = -1; this.kExpXp = -1; }
    if (sc === Scene.Defeat) {
      // 용사의 시련 판 패배: 「시련 실패」 제목, 같은 시련으로 다시 도전(경험치·요령 문구 없음)
      const tId = v.result.trialId ?? '';
      const dKey = v.flow.mapId + '|' + tId;
      if (dKey !== this.kDefeatMap) {
        this.kDefeatMap = dKey;
        this.defeatRetry.textContent = tId ? '다시 도전' : retryLabel(v.flow.mapId);
        this.defeatTitle.textContent = tId ? '시련 실패' : '쓰러졌다…';
        this.defeatTip.style.display = tId ? 'none' : '';
        this.kDefeatVer = -1;
      }
      // 일일 던전·탑 패배(QA F4): 같은 판 재도전이 없다(횟수는 일일 게시판에서) — 「다시 도전」을 숨기고 마을로만
      const dailyDefeat = !!v.result.dailyKind;
      if (dailyDefeat !== this.kDefeatDaily) { this.kDefeatDaily = dailyDefeat; this.defeatRetry.style.display = dailyDefeat ? 'none' : ''; }
      const expPend = expSavePending(gv) ? 1 : 0;
      const tNote = tId ? v.result.trialId + '|' + v.flow.objectiveLabel : '';
      if (this.growth.version !== this.kDefeatVer || (gv ? gv.xp : -1) !== this.kDefeatXp || expPend !== this.kDefeatPend || tNote !== this.kDefeatTrialNote) {
        this.kDefeatTrialNote = tNote;
        this.kDefeatVer = this.growth.version;
        this.kDefeatXp = gv ? gv.xp : -1;
        this.kDefeatPend = expPend;
        const exp = defeatExpLine(gv ? expLine(this.run.summary(gv.xp)) : '', expPend === 1);
        const lines = defeatLines(v.profile, this.growth.goal);
        // 패배해도 이번 판 EXP는 남는다(벌칙 없음). 단 저장이 끝나기 전에는 경고로 말한다
        const tl = tId ? trialResultLines({ ...v.result, cleared: false }) : null;
        this.defeatNote.textContent = tl ? [tl.chip, v.flow.objectiveLabel, tl.defeat].join('\n') : (exp ? [exp, ...lines] : lines).join('\n');
      }
    } else this.kDefeatVer = -1;
  }

  /** 마을 퀘스트 카드의 다음 목표. 성장 목표가 바뀔 때만 새로 만든다 */
  private updateVillageGoal(): void {
    // 환생 자격(Lv99·윤회의 문 퀘스트·다음 모드 공개)이 생기면 그 목표가 먼저다. 환생해 회차가 넘어가거나 자격이 없으면 일반 성장 목표
    const rebirthGoal = rebirthGoalOn(this.view.rebirth);
    const ver = this.growth.version * 2 + (rebirthGoal ? 1 : 0);
    if (ver === this.kVillageVer) return;
    this.kVillageVer = ver;
    this.villageGoalView = rebirthGoal ? rebirthGoalView() : questGoalView(this.growth.goal, this.showTouchSetting);
    villageGoalAction.action = this.villageGoalView?.action ?? null;
  }

  /** 지역 줄(이름·난이도 + 새 기록 표시)과 보상 내역을 값이 바뀔 때만 다시 쓴다 */
  private drawRegionInfo(mapId: MapId, flags: number): void {
    const r = this.view.result;
    const row = this.regionRow;
    row.textContent = '';
    const chip = (text: string, css: string): void => { el('span', 'padding:3px 12px;border-radius:999px;border:1.5px solid;font:900 14px ' + FONT + ';white-space:nowrap;' + css, row, text); };
    const trial = trialResultLines(r);
    if (trial) {
      // 용사의 시련 판: 지역 칩·신기록 대신 시련 칩 하나
      chip(trial.chip, 'background:#1a2238;color:' + C.gold + ';border-color:rgba(242,193,78,.75);');
      return;
    }
    if (r.dailyKind) {
      // 일일 던전·탑(L9): 지역 칩·신기록 대신 일일 칩 하나(일일 판은 지역 기록에 들어가지 않는다 — QA F3)
      chip(r.dailyKind === 'tower' ? '무한의 탑 · ' + (r.dailyFloor ?? 0) + '층' : r.dailyKind === 'vault' ? '일일 · 황금 보물고' : '일일 · 강화석 광산', 'background:#1a2238;color:' + C.gold + ';border-color:rgba(242,193,78,.75);');
      return;
    }
    if (r.raid) {
      // 레이드(RAID-PRD 8절): 지역 칩·지역 신기록 대신 레이드 칩과 주간 보상 칸(레이드 판은 지역 기록에 들어가지 않는다)
      chip(r.raidDiff === 'hard' ? '레이드 · 하드' : '레이드 · 4인 파티', 'background:#3a0e08;color:#ffb070;border-color:#ff7a3a;');
      const n = r.raidN ?? 0, limit = r.raidLimit ?? 3;
      chip(n > 0 ? '주간 보상 ' + (limit - raidLeftOf(r)) + '/' + limit : '연습', n > 0 ? 'background:' + C.gold + ';color:#2a1d05;border-color:#fff3c0;' : 'background:#1a2238;color:#cfd8f5;border-color:#6a7290;');
      return;
    }
    if (isRegionId(mapId)) {
      const info = REGIONS[mapId];
      const st = TIER_STYLE[info.tier === '도전' ? 'hard' : 'easy'];
      chip(info.name + ' · ' + info.tier, 'background:' + st.badgeBg + ';color:' + st.badgeText + ';border-color:' + st.border + ';');
      // 하드 판(D3b): 난이도 칩을 따로 붙인다(PR #176 리뷰 P2)
      if (parseHardStage(r.stageId)) { const h = TIER_STYLE.hard; chip(stageLabel(r.stageId as never), 'background:' + h.badgeBg + ';color:' + h.badgeText + ';border-color:' + h.border + ';'); }
    }
    const gold = 'background:' + C.gold + ';color:#2a1d05;border-color:#fff3c0;';
    if (flags & NEW_FIRST) chip('새 기록 · 첫 클리어', gold);
    else {
      if (flags & NEW_TIME) chip('새 기록 · 최단 ' + fmtBestTime(r.clearTicks), gold);
      if (flags & NEW_STARS) chip('새 기록 · 최고 별 ' + starsText(r.stars), gold);
    }
  }

  private updateResult(): void {
    const r = this.view.result;
    const rewardPending = r.savePending === true;
    // 보상 저장이 성공해도 이번 판 EXP 저장이 실패해 재시도 중이면 '저장 완료'라고 하지 않는다
    const expPending = expSavePending(growthOf(this.view));
    const pending = rewardPending || expPending;
    const saveText = rewardPending
      ? '저장 대기 · 자동으로 재시도하고 있어요.\n저장되기 전에 새로고침하거나 닫으면 미저장 보상을 잃을 수 있어요.' + (expPending ? '\n' + EXP_SAVE_WARNING_SHORT : '')
      : expPending
      ? EXP_SAVE_WARNING
      : r.saveRecovered
        ? '✓ 저장 완료 · 손상된 이전 저장은 따로 백업해 두고 새로 이어서 저장했어요'
      : r.restored && r.pickedCard < 0
        ? '저장된 보상을 이어 받는 중 · 같은 카드 한 장을 고르세요'
        : r.pickedCard < 0
          ? '✓ 저장 완료 · 다음에도 보상을 이어서 받을 수 있어요'
          : r.trialId ? (r.trialNew ? '✓ 저장 완료 · 시련 보상이 반영됐어요' : '✓ 저장 완료 · 시련 기록을 남겼어요')
          : r.dailyKind ? '✓ 저장 완료 · 일일 보상이 반영됐어요' : r.raid ? (r.raidNote ? '✓ 4인 토벌 · 보상은 우편함으로 가요' : '✓ 저장 완료 · 레이드 보상이 반영됐어요') : '✓ 저장 완료 · 고른 카드가 반영됐어요';
    if (saveText !== this.saveText) {
      this.saveText = saveText; this.saveLine.textContent = saveText;
      this.saveLine.classList.toggle('pending', pending);
    }
    const key = this.view.flow.sceneTick < 2 ? 0 : 1;
    if (this.resultKey === -1) {
      // 결과 화면 진입: 선택을 비우고 클리어 연출 → 결과 표 → 카드 뽑기를 시작한다
      this.picked = -1;
      this.resultKey = key;
      this.beginResultFlow();
    }
    const raid = !!r.raid;
    // 일일 던전·탑(L9): 실제 지급(골드·강화석·주문서·보관 젬)만 보여 준다
    const daily = this.dailyLines(r);
    // 용사의 시련 판(3개월 C7): 경험치·카드 없음, 시련 문구만(카드 뽑기는 game이 pickedCard를 채워 건너뛴다)
    const trial = trialResultLines(r);
    this.resultTitle.textContent = trial ? trial.title : daily ? daily.title : raid ? raidTitleText(r) : r.cleared ? '던전 클리어' : '던전 종료';
    const mapId = this.view.flow.mapId;
    const info = isRegionId(mapId) ? REGIONS[mapId] : null;
    // 하드 판(D3b)은 노말 지역 기록에 들어가지 않으므로 '신기록' 표시도 하지 않는다(PR #176 리뷰 P2)
    const flags = parseHardStage(r.stageId) || r.raid || r.dailyKind || trial ? 0 : this.recordWatch.flags(mapId, r.cleared, r.clearTicks, r.stars);
    const rowMap = mapId + '|' + (r.stageId ?? '') + '|' + (r.dailyKind ?? '') + (r.dailyFloor ?? 0) + '|' + (r.trialId ?? '');
    if (rowMap !== this.kRowMap || flags !== this.kRowFlags || r.clearTicks !== this.kRowTicks || r.stars !== this.kRowStars) {
      this.kRowMap = rowMap; this.kRowFlags = flags; this.kRowTicks = r.clearTicks; this.kRowStars = r.stars;
      this.drawRegionInfo(mapId, flags);
    }
    // 고른 카드가 골드가 아니면(강화석·보석·소모품·장비) 골드 합계 뒤에 「+ 카드 …」로 이어 쓴다
    const pickDesc = !trial && r.pickedCard >= 0 && r.cardLoot ? r.cardLoot[r.pickedCard] ?? '' : '';
    if (pickDesc !== this.kItemDesc) { this.kItemDesc = pickDesc; this.kItemCard = pickDesc !== '' && !pickDesc.startsWith('g:') && (parseCardLoot(pickDesc)?.kind ?? 'gold') !== 'gold'; }
    const itemCard = this.kItemCard;
    const cardGold = r.pickedCard >= 0 && !itemCard ? r.cards[r.pickedCard] : -1;
    if (mapId + '|' + (r.stageId ?? '') !== this.kRewardMap || r.kills !== this.kRewardKills || cardGold !== this.kRewardCard || r.cleared !== this.kRewardCleared || r.resultId !== this.kRewardId || r.specialGold !== this.kRewardSpecial || pickDesc !== this.kRewardDesc || (trial ? trial.line : daily ? daily.reward : '') !== this.kRewardDaily) {
      this.kRewardMap = mapId + '|' + (r.stageId ?? ''); this.kRewardKills = r.kills; this.kRewardCard = cardGold; this.kRewardCleared = r.cleared; this.kRewardId = r.resultId; this.kRewardSpecial = r.specialGold; this.kRewardDesc = pickDesc; this.kRewardDaily = trial ? trial.line : daily ? daily.reward : '';
      // 레이드(RAID-PRD 6절): 카드 없이 확정 보상 — 주 n/3·골드 줄과 세트·전설·강화석·보호 주문서 줄
      const rl = raid ? raidResultLines(r, (id) => equipDef(id)?.name ?? id) : null;
      this.rewardLine.textContent = trial ? trial.line : daily ? daily.reward : rl ? rl.reward : info && r.cleared
        ? '보상 내역 · ' + rewardBreakdownText(rewardBreakdown(info, r.kills, cardGold, r.clearMult, r.firstGold, goldDiffOf(r.stageId), r.specialGold)) + (itemCard ? ' + 카드 ' + cardLine(pickDesc, 0, (id) => equipDef(id)?.name ?? id) : '')
        : '';
      const bonus = trial ? trial.chest : daily ? '' : rl ? rl.items : r.cleared ? firstClearItemsText(r) : '';
      this.bonusLine.textContent = bonus;
      this.bonusLine.style.display = bonus ? 'block' : 'none';
      // 레이드 하드 첫 클리어 띠(C13)
      this.raidFirstBand.textContent = rl ? rl.firstBand : '';
      this.raidFirstBand.style.display = rl && rl.firstBand ? 'block' : 'none';
      this.raidFirstItems.textContent = rl ? rl.firstItems : '';
      this.raidFirstItems.style.display = rl && rl.firstItems ? 'block' : 'none';
    }
    // 스테이지 판(D3a)은 '다시 도전 (3-4)', 자유 입장은 지역 이름
    const retryKey = mapId + '|' + (r.stageId ?? '') + '|' + (r.trialId ?? '');
    if (retryKey !== this.kRetryMap) {
      this.kRetryMap = retryKey;
      this.retryBtn.textContent = trial ? '다시 도전' : raid ? '다시 도전 (레이드)' : r.stageId ? '다시 도전 (' + stageLabel(r.stageId as never) + ')' : retryLabel(mapId);
    }
    // 다음 스테이지 버튼: 같은 지역의 다음 스테이지가 열렸고 마을 이동이 예약되지 않았을 때만
    // 무한의 탑(L9): 깬 층 다음 층으로 바로 이어 오른다(같은 날 이어하기). 일일 판은 '다시 도전' 대신 마을로(횟수는 일일 게시판에서)
    const tower = r.cleared && r.dailyKind === 'tower' && (r.dailyNext ?? 0) > 0 ? 'tower:' + r.dailyNext : '';
    const next = trial ? '' : tower || (r.cleared && r.nextStage && !(r.travelTo > 0) ? r.nextStage : '');
    this.retryBtn.style.display = r.dailyKind ? 'none' : '';
    if (next !== this.kNext) {
      this.kNext = next;
      this.nextBtn.style.display = next ? '' : 'none';
      if (tower) this.nextBtn.textContent = '다음 층 (' + r.dailyNext + '층)';
      else if (next) this.nextBtn.textContent = '다음 ' + stageLabel(next as never);
      this.resultBtns.style.gap = next ? '12px' : '20px';
      for (const b of [this.retryBtn, this.villageBtn, this.nextBtn]) b.style.padding = next ? '12px 22px' : '';
    }
    const gv = growthOf(this.view);
    if (gv && (gv.xp !== this.kExpXp || gv.level !== this.kExpLv)) {
      this.kExpXp = gv.xp; this.kExpLv = gv.level;
      this.expText = expLine(this.run.summary(gv.xp));
      this.expLine.textContent = this.expText;
      this.expLine.style.display = this.expText ? 'block' : 'none';
    }
    const picked = r.pickedCard >= 0;
    if (this.growth.version !== this.kResultVer || r.cleared !== this.kResultCleared || (r.trialId ?? '') !== this.kResultTrial) {
      this.kResultVer = this.growth.version; this.kResultCleared = r.cleared; this.kResultTrial = r.trialId ?? '';
      // 카드 선택 전에는 카드를 먼저 고르라고만 알린다. 선택 뒤 목표가 없으면 기존 문구를 그대로 쓴다
      this.resultSub.textContent = trial ? '' : resultLines(this.growth.goal, picked, this.showTouchSetting) || (r.cleared ? '어둠은 물러가고, 더 강한 당신이 다시 나아갑니다.' : '');
      const label = r.travelTo > 0 && picked ? '다음 마을로' : villageButtonLabel(this.growth.goal, picked);
      if (label !== this.kVillageLabel) { this.kVillageLabel = label; this.villageBtn.textContent = label; }
    }
    const mask = Number.isFinite(r.starMask) ? r.starMask : -1;
    for (let i = 0; i < 3; i++) {
      const on = mask >= 0 ? (mask & (1 << i)) !== 0 : i < r.stars;
      const st = this.stars[i];
      st.style.color = on ? C.gold : '#3a4260';
      st.style.textShadow = on ? '0 3px 0 #7a4a00,0 0 16px rgba(255,200,80,.8)' : '0 3px 0 #0a0e1c';
      st.classList.toggle('on', on); // 그림 별(rewardStyle .dh-result-star): 글자 ★는 그림을 못 읽을 때의 대체
      this.starNotes[i].style.color = on ? '#ffe7a8' : '#6a7290';
    }
    this.stat.time.textContent = fmtTime(r.clearTicks);
    this.stat.combo.textContent = fmtNum(r.maxCombo);
    this.stat.kills.textContent = fmtNum(r.kills);
    this.stat.dmg.textContent = fmtNum(r.damageDealt);
    this.stat.gold.textContent = fmtNum(resultGoldStat(r.gold, r.specialGold));
    const done = r.pickedCard >= 0;
    const hintGold = done ? r.cards[r.pickedCard] : -1;
    if (!this.kHintOk || raid !== this.kHintRaid || done !== this.kHintDone || this.phase !== this.kHintPhase || r.pickedCard !== this.kHintPicked || hintGold !== this.kHintGold || pickDesc !== this.kHintDesc || retryKey !== this.kHintMap) {
      this.kHintOk = true; this.kHintRaid = raid; this.kHintDone = done; this.kHintPhase = this.phase; this.kHintPicked = r.pickedCard; this.kHintGold = hintGold; this.kHintDesc = pickDesc; this.kHintMap = retryKey;
      const hint = raid || trial ? '' : done
        ? '보상을 획득했습니다' + (pickDesc !== '' || hintGold > 0 ? ' · ' + cardLine(pickDesc, hintGold, (id) => equipDef(id)?.name ?? id) : '')
        : this.phase === 'cards' ? '카드 한 장을 고르면 이동할 수 있습니다'
        : '잠시 후 보상 카드가 날아옵니다' + (info ? ' · ' + rewardText(info, goldDiffOf(r.stageId)).card.replace(/^카드 /, '골드 카드 ') + ' · 강화석·장비도 나와요' : '');
      this.cardHint.textContent = hint; this.cardHint.style.display = hint ? '' : 'none';
    }
    if (this.retryBtn.disabled === done) {
      this.retryBtn.disabled = this.villageBtn.disabled = this.nextBtn.disabled = !done;
      this.resultBtns.style.opacity = done ? '1' : '0.45';
      this.retryBtn.style.cursor = this.villageBtn.style.cursor = this.nextBtn.style.cursor = done ? 'pointer' : 'not-allowed';
    }
    // 카드 뽑기 화면: 카운트다운은 프레임마다 숫자 한 줄만 갱신한다
    if (this.phase === 'cards' && this.pickStage) {
      // 다른 탭이 먼저 고른 카드가 저장에서 이겼으면 그 칸으로 공개한다
      if (r.pickedCard >= 0 && this.pickStage.chosenIndex !== r.pickedCard) this.pickStage.reconcile(r.pickedCard);
      this.pickStage.update(this.settingsOpen || this.view.flow.paused);
    }
  }
}
